import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'motion/react';

// Scroll reveal animation component
function ScrollReveal({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
import {
  Users,
  BookOpen,
  ArrowRight,
  Star,
  Plus,
  BadgeCheck,
  CreditCard,
  HelpCircle,
  MessageSquare,
  Video,
  TreePine,
  MessageCircle,
  ChevronDown,
} from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination, Autoplay } from 'swiper/modules';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Auth } from '../components/Auth';
import { Meta } from '../components/Meta';
import Lottie from 'lottie-react';
import { supabase } from '../lib/supabase';
import { DiscoverStrip } from '../components/Home/DiscoverStrip';
import { APP_STORE_URL, PLAY_STORE_URL } from '../constants/appLinks';

export function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const iconUrls = {
    calendar: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/calendar-icon.png',
    conversations: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/conversations-icon.png',
    practitioner: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/pracitioner-icon.png',
    venues: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/venue-icon.png',
    jobs: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/handshake.png',
    community: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/community-group.png',
    verified: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/verified-icon.png',
    livestream: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/camera-livesteam-icon.png',
    courses: 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/book-course-icon.png'
  };
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [liveStreamAnimation, setLiveStreamAnimation] = useState(null);
  const [selectedPath, setSelectedPath] = useState<'family' | 'professional'>('family');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [discoverEvent, setDiscoverEvent] = useState<Record<string, unknown> | null>(null);
  const [discoverPractitioner, setDiscoverPractitioner] = useState<Record<string, unknown> | null>(null);
  const [discoverVenue, setDiscoverVenue] = useState<Record<string, unknown> | null>(null);
  const [discoverLoading, setDiscoverLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const handleGetStarted = () => {
    setShowAuthModal(true);
  };

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      setIsLoggedIn(!!data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setIsLoggedIn(!!session);
    });

    return () => {
      isMounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Load the live stream Lottie animation
    fetch('/lottie-live-stream.json')
      .then(response => response.json())
      .then(data => setLiveStreamAnimation(data))
      .catch(error => console.error('Error loading live stream Lottie animation:', error));
  }, []);

  // Discover: latest approved event, practitioner, and venue (by created_at)
  useEffect(() => {
    async function fetchDiscoverLatest() {
      try {
        const [evRes, prRes, vnRes] = await Promise.all([
          supabase
            .from('events')
            .select(
              `
              id, title, slug, image_url, start_date, location, created_at,
              user:users ( user_type )
            `
            )
            .eq('approval_status', 'approved')
            .or('visibility.eq.public,visibility.is.null')
            .eq('user.user_type', 'professional')
            .order('created_at', { ascending: false })
            .limit(1),
          supabase
            .from('practitioners')
            .select(
              `
              id, title, slug, images, category, address, country, created_at,
              user:users ( full_name, avatar_url, username, user_type, subscription_status )
            `
            )
            .eq('approval_status', 'approved')
            .not('slug', 'is', null)
            .eq('user.user_type', 'professional')
            .eq('user.subscription_status', 'active')
            .order('created_at', { ascending: false })
            .limit(1),
          supabase
            .from('venues')
            .select(
              `
              id, name, slug, images, venue_type, address, country, created_at,
              user:user_id ( username, user_type )
            `
            )
            .eq('approval_status', 'approved')
            .not('slug', 'is', null)
            .eq('user.user_type', 'professional')
            .order('created_at', { ascending: false })
            .limit(1),
        ]);

        if (evRes.error) console.error('Discover events:', evRes.error);
        if (prRes.error) console.error('Discover practitioners:', prRes.error);
        if (vnRes.error) console.error('Discover venues:', vnRes.error);

        setDiscoverEvent(evRes.data?.[0] ?? null);
        setDiscoverPractitioner(prRes.data?.[0] ?? null);
        setDiscoverVenue(vnRes.data?.[0] ?? null);
      } catch (err) {
        console.error('Error fetching discover listings:', err);
      } finally {
        setDiscoverLoading(false);
      }
    }
    fetchDiscoverLatest();
  }, []);

  // Component to render Lottie animation with fallback
  const LottieIcon = ({ 
    animationData, 
    fallbackIcon: FallbackIcon, 
    className = "h-8 w-8" 
  }: { 
    animationData: any; 
    fallbackIcon: any; 
    className?: string; 
  }) => {
    if (animationData) {
      return (
        <Lottie
          animationData={animationData}
          loop={true}
          autoplay={true}
          style={{ width: 64, height: 64 }}
          className="lottie-animation"
        />
      );
    }
    return <FallbackIcon className={className} />;
  };

  const spotlightPractitioners = [
  {
    name: "Bee Ingram",
    title: "Yoga Teacher",
    image: "https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/blog-images/59bed50f-5ccf-4265-87fa-7743af34d361/bee%20ingram%20yoga%20teacher.jpeg",
  },
  {
    name: "Marta De Ferrari",
    title: "Ayurveda Practitioner",
    image: "https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/blog-images/59bed50f-5ccf-4265-87fa-7743af34d361/Marta-ayurveda-practitioner.webp",
  },
  {
    name: "Phoebe Grant",
    title: "Birth Doula",
    image: "https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/blog-images/59bed50f-5ccf-4265-87fa-7743af34d361/Birth%20Doula%20-phoebe%20-grant.jpg",
  }
];


  const benefits = [
  {
    icon: Video,
    title: "Live Streaming",
    description: "Join our interactive wellness sessions multiple times a week with expert practitioners. From yoga and meditation to nutrition talks, engage in real-time with our global community.",
    size: "large",
    animationData: liveStreamAnimation
  },
  {
    icon: Star,
    title: "Connect with Wellness Professionals",
    description: "Discover practitioners, venues, job opportunities and events that align with your Wellness journey.",
    size: "small"
  },
  {
    icon: MessageCircle,
    title: "Build Your Own Community",
    description: "Create and nurture connections with like-minded individuals who share your passion for mindful living.",
    size: "small"
  },
  {
    icon: BookOpen,
    title: "Learn and Grow Together",
    description: "Access comprehensive courses and educational resources designed to deepen your knowledge and enhance your wellness practice.",
    size: "large"
  },
  {
    icon: TreePine,
    title: "Permaculture Tools",
    description: "Discover sustainable living tools and resources to create regenerative ecosystems and live in harmony with nature.",
    size: "small"
  }
];

  const faqs = [
    { question: t('home.faq1Q'), answer: t('home.faq1A') },
    { question: t('home.faq2Q'), answer: t('home.faq2A') },
    { question: t('home.faq3Q'), answer: t('home.faq3A') },
    { question: t('home.faq4Q'), answer: t('home.faq4A') },
    { question: t('home.faq5Q'), answer: t('home.faq5A') },
    { question: t('home.faq6Q'), answer: t('home.faq6A') },
    { question: t('home.faq7Q'), answer: t('home.faq7A') },
    { question: t('home.faq8Q'), answer: t('home.faq8A') },
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      <Meta
        title="Mindful Family - Your Wellness Community"
        description="Join Mindful Family, a wellness community connecting practitioners, enthusiasts, and families. Discover events, recipes, courses, and like-minded people on your wellness journey."
      />

      {/* Author attribution and SEO */}
      <Helmet>
        <meta name="author" content="Mindful Family Team" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Mindful Family",
            "author": {
              "@type": "Organization",
              "name": "Mindful Family",
              "url": "https://mindful.family"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Mindful Family"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "datePublished": "2025-01-15",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Mindful Family Team"
            },
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      {/* Hero Section - Redesigned with full-width background and centered content */}
      <section
        className={`relative py-16 md:py-24 overflow-hidden ${
          isLoggedIn ? 'bg-white' : 'bg-white'
        }`}
      >
        {/* Grain texture overlay */}
        {!isLoggedIn && (
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />
        )}

        {/* Floating background elements */}
        <motion.div
          className="absolute top-0 right-0 w-1/2 h-full opacity-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          transition={{ duration: 1.5 }}
        >
          <motion.div
            className="absolute w-64 h-64 rounded-full bg-accent-text/10 -top-20 -right-20"
            animate={{ y: [0, -20, 0], scale: [1, 1.05, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute w-48 h-48 rounded-full bg-accent-text/10 bottom-40 right-20"
            animate={{ y: [0, 15, 0], scale: [1, 1.03, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          />
        </motion.div>

        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-6 text-center lg:text-left">
              <motion.h1
                className="text-5xl md:text-5xl lg:text-6xl font-gelica leading-tight mb-6"
                style={{ color: '#2F4F3E' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                {t('home.heroTitle')}
              </motion.h1>
              <motion.p
                className="text-base md:text-lg lg:text-xl mb-8 max-w-2xl mx-auto lg:mx-0"
                style={{ color: '#5A6B5A' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                {t('home.heroSubtitle')}
              </motion.p>
              <motion.div
                className="flex flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start items-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-full text-white bg-[#8DA847] hover:bg-[#7A9339] transition-colors shadow-md hover:shadow-lg whitespace-nowrap relative"
                >
                  {t('home.joinTheFamily')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                  <span className="absolute -top-2 -right-2 px-2 py-0.5 text-xs font-medium rounded-full text-white shadow-md" style={{ backgroundColor: '#C4714A' }}>
                    It's free*
                  </span>
                </button>
              </motion.div>
            </div>
            <motion.div
              className="col-span-12 lg:col-span-6 mt-12 lg:mt-0 flex justify-center lg:justify-end items-center"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            >
              <div className="relative flex items-start gap-4">
                {/* Left blob with image */}
                <div className="relative">
                  <motion.div
                    className="absolute -top-4 -left-4 w-24 h-24 bg-accent-text/10 rounded-full"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <div className="relative z-10 w-48 h-56 sm:w-56 sm:h-64 rounded-[2rem] overflow-hidden blob-shape">
                    <img
                      src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/wellness-event-lady-circle.png"
                      alt="Wellness"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                {/* Right blob with image - positioned higher */}
                <div className="relative -mt-12">
                  <motion.div
                    className="absolute -bottom-3 -right-3 w-20 h-20 bg-accent-text/10 rounded-full"
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  />
                  <div className="relative z-10 w-44 h-52 sm:w-52 sm:h-60 rounded-[2rem] overflow-hidden" style={{ borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%' }}>
                    <img
                      src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/MainImgHero.png"
                      alt="Wellness"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Disclaimer */}
          <p className="text-xs text-[#202020]/60 mt-6 text-center lg:text-left">
            {t('home.premiumDisclaimer')}
          </p>
        </div>
      </section>

      {/* Discover — live stream + latest event, practitioner, venue (EventCard-style layout) */}
      <section
        className={`relative py-16 md:py-20 ${
          isLoggedIn ? 'bg-white' : 'bg-white'
        }`}
      >
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold text-content">
              {t('home.discoverShortTitle')}
            </h2>
          </div>

          <DiscoverStrip
            loading={discoverLoading}
            discoverEvent={discoverEvent}
            discoverPractitioner={discoverPractitioner}
            discoverVenue={discoverVenue}
          />

        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-24 ${isLoggedIn ? 'bg-white' : 'bg-[#F3F7EE]'} relative`}>
        {/* Grain texture overlay */}
        {!isLoggedIn && (
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />
        )}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-6" style={{ color: '#2F4F3E' }}>
              {t('home.choosePathTitle')}
            </h2>
            
            {/* Toggle Switch */}
            <div className="flex items-center justify-center mb-8">
              <div className="inline-flex items-center bg-white rounded-full p-1 shadow-md border border-gray-200">
                <button
                  onClick={() => setSelectedPath('family')}
                  className={`px-4 py-2 md:px-6 md:py-3 rounded-full font-bold text-sm md:text-base transition-all duration-200 normal-case ${
                    selectedPath === 'family'
                      ? 'bg-accent-text text-white shadow-sm'
                      : 'text-[#202020] hover:text-[#202020]'
                  }`}
                >
                  {t('home.forYouTitle')}
                </button>
                <button
                  onClick={() => setSelectedPath('professional')}
                  className={`px-4 py-2 md:px-6 md:py-3 rounded-full font-bold text-sm md:text-base transition-all duration-200 normal-case ${
                    selectedPath === 'professional'
                      ? 'bg-accent-text text-white shadow-sm'
                      : 'text-[#202020] hover:text-[#202020]'
                  }`}
                >
                  {t('home.forProfessionalsTitle')}
                </button>
              </div>
            </div>
          </div>

          {/* Content Block */}
          <div className="transition-all duration-300">
            {selectedPath === 'family' ? (
              /* Block A: For you & your family */
              <div className="space-y-6">
                <div className="flex items-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full overflow-hidden mr-4 flex-shrink-0">
                    <img
                      src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-enthausiast.png"
                      alt="Wellness Enthusiast"
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold" style={{ color: '#202020' }}>
                      {t('home.forYouTitle')}
                    </h3>
                    <p className="text-base md:text-base mt-1" style={{ color: '#202020' }}>
                      {t('home.forYouSubtitle')}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                  <div className="md:col-span-7 relative rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="absolute -top-3 left-4 inline-flex items-center px-3 py-1 rounded-full text-white text-xs font-semibold" style={{ backgroundColor: '#c07f5a' }}>
                      <BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
                      {t('home.free')}
                    </div>
                    <p className="text-sm font-semibold mt-4 mb-3" style={{ color: '#2F4F3E' }}>
                      {t('home.forYouDiscoverTitle')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <Link to="/events" className="inline-flex items-center gap-3 px-3 py-2 rounded-2xl bg-white border border-gray-200 text-sm font-medium hover:border-[#c07f5a]/50 hover:bg-[#c07f5a]/5 transition-colors" style={{ color: '#2F4F3E' }}>
                        <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/calendar-icon.png" alt="Events" className="w-8 h-8 flex-shrink-0" />
                        {t('home.forYouDiscoverEvents')}
                      </Link>
                      <Link to="/practitioners" className="inline-flex items-center gap-3 px-3 py-2 rounded-2xl bg-white border border-gray-200 text-sm font-medium hover:border-[#c07f5a]/50 hover:bg-[#c07f5a]/5 transition-colors" style={{ color: '#2F4F3E' }}>
                        <img src={iconUrls.practitioner} alt="Practitioners" className="w-8 h-8 flex-shrink-0" />
                        {t('home.forYouDiscoverPractitioners')}
                      </Link>
                      <Link to="/venues" className="inline-flex items-center gap-3 px-3 py-2 rounded-2xl bg-white border border-gray-200 text-sm font-medium hover:border-[#c07f5a]/50 hover:bg-[#c07f5a]/5 transition-colors" style={{ color: '#2F4F3E' }}>
                        <img src={iconUrls.venues} alt="Venues" className="w-8 h-8 flex-shrink-0" />
                        {t('home.forYouDiscoverVenues')}
                      </Link>
                      <Link to="/jobs" className="inline-flex items-center gap-3 px-3 py-2 rounded-2xl bg-white border border-gray-200 text-sm font-medium hover:border-[#c07f5a]/50 hover:bg-[#c07f5a]/5 transition-colors" style={{ color: '#2F4F3E' }}>
                        <img src={iconUrls.jobs} alt="Jobs" className="w-8 h-8 flex-shrink-0" />
                        {t('home.forYouDiscoverJobs')}
                      </Link>
                      <Link to="/communities" className="inline-flex items-center gap-3 px-3 py-2 rounded-2xl bg-white border border-gray-200 text-sm font-medium sm:col-span-2 hover:border-[#c07f5a]/50 hover:bg-[#c07f5a]/5 transition-colors" style={{ color: '#2F4F3E' }}>
                        <img src={iconUrls.community} alt="Communities" className="w-8 h-8 flex-shrink-0" />
                        {t('home.forYouDiscoverCommunities')}
                      </Link>
                    </div>
                  </div>

                  <div className="md:col-span-5 md:mt-10 relative rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="absolute -top-3 left-4 inline-flex items-center px-3 py-1 rounded-full text-white text-xs font-semibold" style={{ backgroundColor: '#c07f5a' }}>
                      <BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
                      {t('home.free')}
                    </div>
                    <div className="flex items-start gap-3 mt-4">
                      <img src={iconUrls.conversations} alt="Conversations" className="w-8 h-8 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold" style={{ color: '#2F4F3E' }}>
                          {t('home.forYouSupportTitle')}
                        </p>
                        <p className="text-sm mt-1" style={{ color: '#5A6B5A' }}>
                          {t('home.forYouSupportSubtitle')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-7 relative rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="absolute -top-3 left-4 inline-flex items-center px-3 py-1 rounded-full text-white text-xs font-semibold" style={{ backgroundColor: '#c07f5a' }}>
                      <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                      {t('home.paid')}
                    </div>
                    <ul className="space-y-4 mt-4">
                      <li className="flex items-start">
                        <img src={iconUrls.livestream} alt="Live sessions" className="h-8 w-8 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-base" style={{ color: '#2F4F3E' }}>{t('home.forYouPremiumLive')}</span>
                      </li>
                      <li className="flex items-start">
                        <img src={iconUrls.courses} alt="Courses" className="h-8 w-8 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-base" style={{ color: '#2F4F3E' }}>{t('home.forYouPremiumCourses')}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex flex-col items-center gap-3">
                    <div className="inline-flex items-center px-6 py-3 bg-accent-text/10 rounded-full">
                      <Users className="h-5 w-5 text-accent-text mr-2" />
                      <span className="text-sm font-medium" style={{ color: '#2F4F3E' }}>
                        {t('home.startFreeUpgrade')}
                      </span>
                    </div>
                    <Link
                      to="/pricing"
                      className="text-xs px-3 py-1 bg-white/80 rounded-full border border-accent-text/20 hover:bg-accent-text/10 transition-colors"
                      style={{ color: '#5A6B5A' }}
                    >
                      {t('home.viewPricingDetails')}
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              /* Block B: For professionals */
              <div className="space-y-6">
                <div className="flex items-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full overflow-hidden mr-4 flex-shrink-0">
                    <img
                      src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/wellness-practitioner-work.png"
                      alt="Wellness Practitioner"
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold" style={{ color: '#202020' }}>
                      {t('home.forProfessionalsTitle')}
                    </h3>
                    <p className="text-base md:text-base mt-1" style={{ color: '#202020' }}>
                      {t('home.forProfessionalsSubtitle')}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5">
                  <div className="relative rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="absolute -top-3 left-4 inline-flex items-center px-3 py-1 rounded-full text-white text-xs font-semibold" style={{ backgroundColor: '#c07f5a' }}>
                      <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                      {t('home.paid')}
                    </div>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <li className="flex items-start">
                        <img src={iconUrls.practitioner} alt="Practitioner profile" className="h-8 w-8 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-base" style={{ color: '#2F4F3E' }}>{t('home.forProBullet1')}</span>
                      </li>
                      <li className="flex items-start">
                        <img src={iconUrls.community} alt="Communities" className="h-8 w-8 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-base" style={{ color: '#2F4F3E' }}>{t('home.forProBullet2')}</span>
                      </li>
                      <li className="flex items-start">
                        <img src={iconUrls.venues} alt="Events" className="h-8 w-8 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-base" style={{ color: '#2F4F3E' }}>{t('home.forProBullet3')}</span>
                      </li>
                      <li className="flex items-start">
                        <img src={iconUrls.livestream} alt="Pro tools" className="h-8 w-8 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-base" style={{ color: '#2F4F3E' }}>{t('home.forProBullet4')}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex flex-col items-center gap-3">
                    <div className="inline-flex items-center px-6 py-3 bg-accent-text/10 rounded-full">
                      <Users className="h-5 w-5 text-accent-text mr-2" />
                      <span className="text-sm font-medium" style={{ color: '#2F4F3E' }}>
                        {t('home.professionalMembership')}
                      </span>
                    </div>
                    <Link
                      to="/pricing"
                      className="text-xs px-3 py-1 bg-white/80 rounded-full border border-accent-text/20 hover:bg-accent-text/10 transition-colors"
                      style={{ color: '#5A6B5A' }}
                    >
                      {t('home.viewPricingDetails')}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Become a Member Button */}
          <div className="text-center mt-12">
            <button 
              onClick={handleGetStarted}
              className="inline-flex items-center px-8 py-4 text-white bg-accent-text rounded-full shadow-md hover:shadow-lg hover:bg-accent-text/90 transition-all text-lg font-medium whitespace-nowrap"
            >
              {t('home.becomeMember')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Live Stream Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        {!isLoggedIn && <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#F3F7EE] to-transparent"></div>}
        <div className="absolute -left-32 top-1/4 w-64 h-64 rounded-full bg-accent-text/5"></div>
        
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-red-50 text-red-600 rounded-full mb-6">
                <span className="animate-pulse h-2 w-2 bg-red-600 rounded-full mr-2"></span>
                <span className="text-sm font-medium">LIVE NOW</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-6" style={{ color: '#2F4F3E' }}>
                {t('home.liveSessionsTitle')}
              </h2>
              
              <p className="text-base md:text-lg lg:text-xl mb-8 leading-relaxed" style={{ color: '#5A6B5A' }}>
                {t('home.liveSessionsSubtitle')}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-accent-text/10 rounded-lg mr-4">
                    <img src={iconUrls.calendar} alt="Regular schedule" className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1" style={{ color: '#2F4F3E' }}>{t('home.regularSchedule')}</h3>
                    <p className="text-base" style={{ color: '#5A6B5A' }}>{t('home.regularScheduleDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-accent-text/10 rounded-lg mr-4">
                    <img src={iconUrls.verified} alt="Expert instruction" className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-medium text-base mb-1" style={{ color: '#2F4F3E' }}>{t('home.expertInstructors')}</h3>
                    <p className="text-base" style={{ color: '#5A6B5A' }}>{t('home.expertInstructorsDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-accent-text/10 rounded-lg mr-4">
                    <img src={iconUrls.conversations} alt="Interactive chat" className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-medium text-base mb-1" style={{ color: '#2F4F3E' }}>{t('home.interactiveChat')}</h3>
                    <p className="text-base" style={{ color: '#5A6B5A' }}>{t('home.interactiveChatDesc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 p-2 bg-accent-text/10 rounded-lg mr-4">
                    <img src={iconUrls.courses} alt="Diverse topics" className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-medium text-base mb-1" style={{ color: '#2F4F3E' }}>{t('home.diverseTopics')}</h3>
                    <p className="text-base" style={{ color: '#5A6B5A' }}>{t('home.diverseTopicsDesc')}</p>
                  </div>
                </div>
              </div>
              
              <Link
                to="/live-stream"
                className="inline-flex items-center px-8 py-4 text-white bg-accent-text rounded-full shadow-md hover:shadow-lg hover:bg-accent-text/90 transition-all whitespace-nowrap"
              >
                {t('home.joinLiveSessions')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            
            <div className="relative grid grid-cols-3 gap-4 items-center">
              {/* Left video - live stream woman - smaller */}
              <div className="relative rounded-xl overflow-hidden shadow-xl col-span-1">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-40 object-cover"
                >
                  <source src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/live-stream-woman.mp4" type="video/mp4" />
                </video>
                <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-medium z-20 animate-pulse">
                  LIVE
                </div>
              </div>
              {/* Right video - message animation - bigger, higher */}
              <div className="relative rounded-xl overflow-hidden col-span-2">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-72 object-cover rounded-lg"
                >
                  <source src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/123c446f-e80c-409d-a3d3-e6fdc14949d4/messageani.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>

          {/* Research Box */}
          <div className="mt-16 max-w-3xl mx-auto">
            <div className="bg-[#F3F7EE] rounded-xl p-6 md:p-8 border border-[#8DA847]/20">
              <p className="text-base md:text-lg leading-relaxed" style={{ color: '#2F4F3E' }}>
                Research from the <strong>Universities of Bath and Southampton</strong> shows 10 minutes of <a href="https://grokipedia.com/page/Mindfulness" target="_blank" rel="noopener noreferrer" className="text-[#8DA847] hover:underline font-medium">mindfulness</a> a day reduces depression by 19%. Our live sessions make it easy to build that habit.
              </p>
              <a
                href="https://www.bath.ac.uk/announcements/just-10-minutes-of-mindfulness-daily-boosts-wellbeing-and-fights-depression/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center mt-4 text-sm font-medium text-[#8DA847] hover:underline"
              >
                Read the research
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Practitioners */}
      <section className={`py-24 ${isLoggedIn ? 'bg-white' : 'bg-[#F3F7EE]'} relative`}>
        {/* Grain texture overlay */}
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-6" style={{ color: '#2F4F3E' }}>
              {t('home.featuredPractitioners')}
            </h2>
            <p className="text-base md:text-lg lg:text-xl max-w-3xl mx-auto" style={{ color: '#5A6B5A' }}>
              {t('home.featuredPractitionersSubtitle')}
            </p>
          </ScrollReveal>

          {/* Swiper Carousel */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {spotlightPractitioners.map((practitioner, index) => (
              <ScrollReveal key={index} delay={index * 0.15}>
                <div
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 group h-full"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={practitioner.image}
                    alt={practitioner.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-6 relative">
                  <div className="absolute -top-4 left-6 bg-accent-text text-white px-4 py-2 rounded-lg shadow-md z-10">
                    <span className="font-medium text-sm">{practitioner.title}</span>
                  </div>
                  <h3 className="text-xl font-bold mt-6 mb-3" style={{ color: '#2F4F3E' }}>
                    {practitioner.name}
                  </h3>
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: '#2F4F3E', opacity: 0.6 }}>Verified Expert</span>
                    <button className="text-accent-text hover:text-accent-text/80 text-sm font-medium">
                      View Profile
                    </button>
                  </div>
                </div>
              </div>
              </ScrollReveal>
            ))}
          </div>
          
          {/* Mobile Swiper */}
          <div className="md:hidden">
            <Swiper
              slidesPerView={1.2}
              centeredSlides={true}
              spaceBetween={20}
              pagination={{ clickable: true }}
              modules={[Pagination, Autoplay]}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              loop={true}
              className="pb-12"
            >
              {spotlightPractitioners.map((practitioner, index) => (
                <SwiperSlide key={index}>
                  <div className="bg-white rounded-xl shadow-md overflow-hidden h-full">
                    <div className="relative h-48">
                      <img
                        src={practitioner.image}
                        alt={practitioner.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent h-1/2"></div>
                      <div className="absolute bottom-4 left-4 text-white">
                        <span className="font-medium text-sm bg-accent-text/80 px-2 py-1 rounded">
                          {practitioner.title}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-semibold mb-1" style={{ color: '#2F4F3E' }}>
                        {practitioner.name}
                      </h3>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs" style={{ color: '#2F4F3E', opacity: 0.6 }}>Verified Expert</span>
                        <button className="text-accent-text hover:text-accent-text/80 text-xs font-medium">
                          View Profile
                        </button>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/practitioners"
              className="inline-flex items-center px-6 py-3 border-2 border-accent-text text-accent-text hover:bg-accent-text hover:text-white rounded-full transition-colors font-medium whitespace-nowrap"
            >
              {t('home.viewAllPractitioners')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={`py-24 ${isLoggedIn ? 'bg-white' : 'bg-[#F3F7EE]'} relative`}>
        {/* Grain texture overlay */}
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-6" style={{ color: '#2F4F3E' }}>
              {t('home.faqTitle')}
            </h2>
            <p className="text-base md:text-lg lg:text-xl" style={{ color: '#5A6B5A' }}>
              {t('home.faqSubtitle')}
            </p>
          </div>

          {/* Quick Answer Box */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-12 border border-accent-text/10 shadow-sm">
            <h3 className="text-lg font-semibold mb-3" style={{ color: '#2F4F3E' }}>
              What is Mindful Family?
            </h3>
            <p className="text-base leading-relaxed" style={{ color: '#5A6B5A' }}>
              Mindful Family is a free platform for wellness. Find verified practitioners, live sessions, local events and communities in one place. It works for individuals, families and businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-accent-text/10 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold pr-4" style={{ color: '#2F4F3E' }}>
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`h-5 w-5 text-accent-text flex-shrink-0 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${openFaqIndex === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <p className="px-6 pb-6 text-base leading-relaxed" style={{ color: '#5A6B5A' }}>
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Begin Your Wellness Journey Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        {!isLoggedIn && <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#F3F7EE] to-transparent"></div>}
        <div className="absolute -right-32 top-1/4 w-64 h-64 rounded-full bg-accent-text/5"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-6" style={{ color: '#2F4F3E' }}>
            {t('home.ctaTitle')}
          </h2>
            <p className="text-base md:text-lg lg:text-xl mb-12 max-w-2xl mx-auto" style={{ color: '#5A6B5A' }}>
            {t('home.ctaSubtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button 
              onClick={handleGetStarted}
              className="inline-flex items-center px-8 py-4 text-white bg-accent-text rounded-full shadow-md hover:shadow-lg hover:bg-accent-text/90 transition-all text-lg font-medium whitespace-nowrap"
            >
              {t('home.joinOurCommunity')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <Link 
              to="/live-stream"
              className="inline-flex items-center px-8 py-4 border-2 border-accent-text text-accent-text hover:bg-accent-text hover:text-white rounded-full transition-colors text-lg font-medium whitespace-nowrap"
            >
              {t('home.watchLiveSessions')}
            </Link>
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section
        className={`py-20 relative overflow-hidden ${isLoggedIn ? 'bg-white' : 'bg-white'}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-gelica font-bold mb-4" style={{ color: '#2F4F3E' }}>
              {t('home.downloadTitle')}
            </h2>
            <p className="text-base md:text-lg" style={{ color: '#5A6B5A' }}>
              {t('home.downloadSubtitle')}
            </p>
          </div>
          
          <div className="flex flex-row items-center justify-center gap-4 sm:gap-6">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 transition-opacity transform hover:scale-105"
              title="Download on the App Store"
            >
              <div className="w-48 h-14 bg-black rounded-xl flex items-center justify-center px-4 hover:bg-gray-800 transition-colors shadow-lg">
                <svg className="w-7 h-7 text-white mr-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs text-white/80 leading-tight">Download on the</div>
                  <div className="text-sm font-semibold text-white leading-tight">App Store</div>
                </div>
              </div>
            </a>
            <a
              href={PLAY_STORE_URL}
              className="inline-block hover:opacity-80 transition-opacity transform hover:scale-105"
              title="Get it on Google Play"
            >
              <div className="w-48 h-14 bg-black rounded-xl flex items-center justify-center px-4 hover:bg-gray-800 transition-colors shadow-lg">
                <svg className="w-7 h-7 text-white mr-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs text-white/80 leading-tight">GET IT ON</div>
                  <div className="text-sm font-semibold text-white leading-tight">Google Play</div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      {showAuthModal && (
        <Auth onClose={() => setShowAuthModal(false)} />
      )}
    </div>
  );
}